// src/utils/Onboarding/onboardingFields.js

/**
 * Canonical field catalogue for onboarding data capture.
 * Each field definition describes what the value represents, the preferred data type,
 * and the default value to surface before the user provides input.
 */
export const ONBOARDING_FIELD_DEFINITIONS = Object.freeze({
  sessionIds: {
    key: 'sessionIds',
    label: 'Linked Tracking Session IDs',
    description: 'One or more tracking session IDs associated with this onboarding run.',
    valueType: 'array',
    allowsMultipleEntries: true,
    initialValue: [],
  },
  selfieStorageLocations: {
    key: 'selfieStorageLocations',
    label: 'Selfie Storage Locations',
    description: 'Firebase Storage paths for every selfie uploaded during onboarding.',
    valueType: 'array',
    allowsMultipleEntries: true,
    initialValue: [],
  },
  selfieAnalysisSummaries: {
    key: 'selfieAnalysisSummaries',
    label: 'Selfie Analysis Summaries',
    description: 'Structured summaries returned from the hair analysis pipeline for each uploaded selfie.',
    valueType: 'array',
    allowsMultipleEntries: true,
    initialValue: [],
  },
  // Latest analysis-derived fields (single, last known values)
  lastAnalysisBaldnessBucket: {
    key: 'lastAnalysisBaldnessBucket',
    label: 'Last Analysis Baldness Bucket',
    description: 'Last detected baldness_bucket from the selfie analysis (none, minimal, moderate, severe, full_bald).',
    valueType: 'string',
    initialValue: null,
  },
  lastAnalysisHairlineBucket: {
    key: 'lastAnalysisHairlineBucket',
    label: 'Last Analysis Hairline Bucket',
    description: 'Last detected hairline_bucket from the selfie analysis (none, minimal, moderate, severe, full_bald).',
    valueType: 'string',
    initialValue: null,
  },
  lastAnalysisConfidence: {
    key: 'lastAnalysisConfidence',
    label: 'Last Analysis Confidence',
    description: 'Confidence score from the latest selfie analysis.',
    valueType: 'number',
    initialValue: null,
  },
  lastAnalysisNorwoodScore: {
    key: 'lastAnalysisNorwoodScore',
    label: 'Last Analysis Norwood Score',
    description: 'Computed Norwood-inspired score (1-7) based on the latest analysis buckets.',
    valueType: 'number',
    initialValue: null,
  },
  lastAnalysisHairColor: {
    key: 'lastAnalysisHairColor',
    label: 'Last Analysis Hair Color',
    description: 'Hair color detected/returned by the analysis (if available).',
    valueType: 'string',
    initialValue: null,
  },
  lastAnalysisHairType: {
    key: 'lastAnalysisHairType',
    label: 'Last Analysis Hair Type',
    description: 'Hair type detected/returned by the analysis (if available).',
    valueType: 'string',
    initialValue: null,
  },
  lastAnalysisSkinType: {
    key: 'lastAnalysisSkinType',
    label: 'Last Analysis Skin Type',
    description: 'Skin/ethnicity type detected/returned by the analysis (if available).',
    valueType: 'string',
    initialValue: null,
  },
  birthdateIso8601: {
    key: 'birthdateIso8601',
    label: 'Birthdate (ISO 8601)',
    description: 'Birthdate collected from the user, stored as an ISO 8601 date string (YYYY-MM-DD).',
    valueType: 'string',
    initialValue: null,
  },
  calculatedAgeInYears: {
    key: 'calculatedAgeInYears',
    label: 'Calculated Age (Years)',
    description: 'Age calculated from the supplied birthdate. Stored as an integer number of years.',
    valueType: 'number',
    initialValue: null,
  },
  countryOfResidence: {
    key: 'countryOfResidence',
    label: 'Country of Residence',
    description: 'Country selected by the user, including custom entries for the "Other" path.',
    valueType: 'string',
    initialValue: null,
  },
  countryVisaNotRequired: {
    key: 'countryVisaNotRequired',
    label: 'Country Visa Not Required',
    description: 'Whether the user\'s country of residence does not require a visa to enter Turkey.',
    valueType: 'boolean',
    initialValue: null,
  },
  hasTurkeyVisa: {
    key: 'hasTurkeyVisa',
    label: 'Has Turkey Visa',
    description: 'Whether the user currently has a visa or e-visa to enter Turkey (asked only if visa is required).',
    valueType: 'string',
    initialValue: null,
  },
  hairProblemsIdentified: {
    key: 'hairProblemsIdentified',
    label: 'Hair Problems Identified',
    description: 'Problems the user is experiencing with their hair (multiselect: hairline problems, density issues, bald spots, hair fall).',
    valueType: 'array',
    allowsMultipleEntries: true,
    initialValue: [],
  },
  hairLossOnsetTiming: {
    key: 'hairLossOnsetTiming',
    label: 'Hair Loss Onset Timing',
    description: 'How long ago the user first noticed their hair loss.',
    valueType: 'string',
    initialValue: null,
  },
  lifeWithoutHairLossPerception: {
    key: 'lifeWithoutHairLossPerception',
    label: 'Life Without Hair Loss Perception',
    description: 'How different life would be without hair loss according to the user.',
    valueType: 'string',
    initialValue: null,
  },
  dailyHairLossThoughtFrequency: {
    key: 'dailyHairLossThoughtFrequency',
    label: 'Daily Hair Loss Thought Frequency',
    description: 'How often the user thinks about hair loss each day.',
    valueType: 'string',
    initialValue: null,
  },
  perceivedSocialImpactFromHairLoss: {
    key: 'perceivedSocialImpactFromHairLoss',
    label: 'Perceived Social Impact From Hair Loss',
    description: 'Whether the user feels others act differently towards them due to hair loss.',
    valueType: 'string',
    initialValue: null,
  },
  hairLossStressLevel: {
    key: 'hairLossStressLevel',
    label: 'Hair Loss Stress Level',
    description: 'How stressed the user feels because of hair loss.',
    valueType: 'string',
    initialValue: null,
  },
  actionsTakenAgainstHairLoss: {
    key: 'actionsTakenAgainstHairLoss',
    label: 'Actions Taken Against Hair Loss',
    description: 'Steps the user has already taken to combat hair loss.',
    valueType: 'array',
    allowsMultipleEntries: true,
    initialValue: [],
  },
  manualNorwoodScore: {
    key: 'manualNorwoodScore',
    label: 'Manual Norwood Score',
    description: 'User-selected Norwood-inspired score/stage representing current hair loss.',
    valueType: 'string',
    initialValue: null,
  },
  manualSkinType: {
    key: 'manualSkinType',
    label: 'Manual Skin Type',
    description: 'User-identified skin/ethnicity type to personalize recommendations.',
    valueType: 'string',
    initialValue: null,
  },
  manualHairType: {
    key: 'manualHairType',
    label: 'Manual Hair Type',
    description: 'User-reported hair texture (e.g., straight, wavy, curly).',
    valueType: 'string',
    initialValue: null,
  },
  manualHairColor: {
    key: 'manualHairColor',
    label: 'Manual Hair Color',
    description: 'User-reported natural/primary hair color.',
    valueType: 'string',
    initialValue: null,
  },
  travelAbilityToTurkey: {
    key: 'travelAbilityToTurkey',
    label: 'Travel Ability to Turkey',
    description: 'Whether the user can travel to Turkey for treatment.',
    valueType: 'string',
    initialValue: null,
  },
  idealOperationTimeline: {
    key: 'idealOperationTimeline',
    label: 'Ideal Operation Timeline',
    description: 'When the user ideally wants to get the hair transplant operation.',
    valueType: 'string',
    initialValue: null,
  },
  budgetRangeForProcedure: {
    key: 'budgetRangeForProcedure',
    label: 'Budget Range for Procedure',
    description: 'Maximum budget the user can allocate for the procedure.',
    valueType: 'string',
    initialValue: null,
  },
  previousHairTransplantHistory: {
    key: 'previousHairTransplantHistory',
    label: 'Previous Hair Transplant History',
    description: 'Whether the user has undergone a hair transplant before.',
    valueType: 'string',
    initialValue: null,
  },
  clinicImportancePreferences: {
    key: 'clinicImportancePreferences',
    label: 'Clinic Importance Preferences',
    description: 'Multiple selection of clinic traits the user finds important.',
    valueType: 'array',
    allowsMultipleEntries: true,
    initialValue: [],
  },
  videoConsultationPreference: {
    key: 'videoConsultationPreference',
    label: 'Video Consultation Preference',
    description: 'Whether the user would like to schedule a video consultation with the doctor.',
    valueType: 'string',
    initialValue: null,
  },
  medicationComfortLevel: {
    key: 'medicationComfortLevel',
    label: 'Medication Comfort Level',
    description: 'How comfortable the user is with using medication as part of their treatment.',
    valueType: 'string',
    initialValue: null,
  },
  fullLegalName: {
    key: 'fullLegalName',
    label: 'Full Legal Name',
    description: 'Full name supplied by the user.',
    valueType: 'string',
    initialValue: '',
  },
  aestheticPreferenceLikes: {
    key: 'aestheticPreferenceLikes',
    label: 'Aesthetic Preference Likes',
    description: 'Images or descriptors the user liked during the aesthetic goals slide.',
    valueType: 'array',
    allowsMultipleEntries: true,
    initialValue: [],
  },
  aestheticPreferenceDislikes: {
    key: 'aestheticPreferenceDislikes',
    label: 'Aesthetic Preference Dislikes',
    description: 'Images or descriptors the user disliked during the aesthetic goals slide.',
    valueType: 'array',
    allowsMultipleEntries: true,
    initialValue: [],
  },
  primaryContactPhoneNumber: {
    key: 'primaryContactPhoneNumber',
    label: 'Primary Contact Phone Number',
    description: 'Full international phone number string the user provided for follow up.',
    valueType: 'string',
    initialValue: null,
  },
  primaryContactDialCode: {
    key: 'primaryContactDialCode',
    label: 'Primary Contact Dial Code',
    description: 'E.164 dial prefix captured from the selected country.',
    valueType: 'string',
    initialValue: null,
  },
  primaryContactPhoneRegion: {
    key: 'primaryContactPhoneRegion',
    label: 'Primary Contact Phone Region',
    description: 'ISO country code associated with the chosen phone number.',
    valueType: 'string',
    initialValue: null,
  },
  primaryContactEmailAddress: {
    key: 'primaryContactEmailAddress',
    label: 'Primary Contact Email Address',
    description: 'Email address provided by the user for follow up.',
    valueType: 'string',
    initialValue: null,
  },
  preferredFollowUpChannel: {
    key: 'preferredFollowUpChannel',
    label: 'Preferred Follow-up Channel',
    description: 'The communication channel the user prefers for follow up (e.g., phone, email).',
    valueType: 'string',
    initialValue: null,
  },
  // Medical Background Questions (13 fields)
  genetic_heart_conditions: {
    key: 'genetic_heart_conditions',
    label: 'Genetic Heart Conditions',
    description: 'Whether the user has any genetic heart conditions in their family.',
    valueType: 'string',
    initialValue: null,
  },
  allergies: {
    key: 'allergies',
    label: 'Allergies',
    description: 'Whether the user has any allergies (food, medicine, latex, etc.).',
    valueType: 'string',
    initialValue: null,
  },
  chronic_illness: {
    key: 'chronic_illness',
    label: 'Chronic Illness',
    description: 'Whether the user has any chronic illness (heart disease, diabetes, etc.).',
    valueType: 'string',
    initialValue: null,
  },
  regular_medication: {
    key: 'regular_medication',
    label: 'Regular Medication Use',
    description: 'Whether the user uses any medication regularly (insulin, anticoagulants, etc.).',
    valueType: 'string',
    initialValue: null,
  },
  contagious_disease: {
    key: 'contagious_disease',
    label: 'Contagious Disease History',
    description: 'Whether the user has or had any contagious disease (Hepatitis, HIV, etc.).',
    valueType: 'string',
    initialValue: null,
  },
  keloid_scars: {
    key: 'keloid_scars',
    label: 'Keloid Scars',
    description: 'Whether the user has any keloid scars on their skin.',
    valueType: 'string',
    initialValue: null,
  },
  needle_phobia: {
    key: 'needle_phobia',
    label: 'Needle Phobia',
    description: 'Whether the user has a needle phobia.',
    valueType: 'string',
    initialValue: null,
  },
  low_pain_threshold: {
    key: 'low_pain_threshold',
    label: 'Low Pain Threshold',
    description: 'Whether the user has a low pain threshold.',
    valueType: 'string',
    initialValue: null,
  },
  smoker: {
    key: 'smoker',
    label: 'Smoking Status',
    description: 'Whether the user is a smoker.',
    valueType: 'string',
    initialValue: null,
  },
  drug_addiction: {
    key: 'drug_addiction',
    label: 'Drug Addiction',
    description: 'Whether the user is addicted to any drugs.',
    valueType: 'string',
    initialValue: null,
  },
  previous_surgery: {
    key: 'previous_surgery',
    label: 'Previous Surgery',
    description: 'Whether the user has had any surgery before.',
    valueType: 'string',
    initialValue: null,
  },
  recent_anesthesia: {
    key: 'recent_anesthesia',
    label: 'Recent Anesthesia',
    description: 'Whether the user has had local or general anesthesia in the last 2 years.',
    valueType: 'string',
    initialValue: null,
  },
  anemia_diagnosis: {
    key: 'anemia_diagnosis',
    label: 'Anemia Diagnosis',
    description: 'Whether the user has ever been diagnosed with anemia.',
    valueType: 'string',
    initialValue: null,
  },
  userNavLog: {
    key: 'userNavLog',
    label: 'User Navigation Log',
    description: 'Chronological array of step numbers representing the user\'s navigation path through onboarding, including backtracks.',
    valueType: 'array',
    allowsMultipleEntries: true,
    initialValue: [],
  },
  faultyUploads: {
    key: 'faultyUploads',
    label: 'Faulty Uploads',
    description: 'Array of upload attempts that failed validation (e.g., no face detected, processing errors). Each entry contains storagePath, errorType, errorMessage, and uploadedAt.',
    valueType: 'array',
    allowsMultipleEntries: true,
    initialValue: [],
  },
  saveContacts: {
    key: 'saveContacts',
    label: 'Save Report Contacts',
    description: 'Array of contact entries captured via SaveReportPopup. Each entry contains slideNumber, slideName, method (phone/email), value, timestamp, and optionally dialCode/countryCode.',
    valueType: 'array',
    allowsMultipleEntries: true,
    initialValue: [],
  },
  contactButtonClicks: {
    key: 'contactButtonClicks',
    label: 'Contact Button Clicks',
    description: 'Array of contact button click events (WhatsApp, Messenger, iMessage, Email). Each entry contains slideName, slideNumber, buttonType, href, timestamp.',
    valueType: 'array',
    allowsMultipleEntries: true,
    initialValue: [],
  },
});

export const ONBOARDING_FIELD_KEYS = Object.freeze(Object.keys(ONBOARDING_FIELD_DEFINITIONS));

export function getOnboardingFieldDefinition(fieldKey) {
  return ONBOARDING_FIELD_DEFINITIONS[fieldKey] || null;
}

export function createEmptyFieldState(fieldKey) {
  const definition = getOnboardingFieldDefinition(fieldKey);
  if (!definition) {
    throw new Error(`Unknown onboarding field: ${fieldKey}`);
  }

  return {
    key: fieldKey,
    definition,
    history: [],
    latestValue: definition.initialValue ?? null,
    latestUpdatedAt: null,
    lastValueSource: null,
    firstFilledAt: null,
    fillCount: 0,
    status: 'never_filled',
  };
}

export function createInitialFieldsState() {
  const fields = {};
  for (const key of ONBOARDING_FIELD_KEYS) {
    fields[key] = createEmptyFieldState(key);
  }
  return fields;
}
